import EntityListCard from '@repo/ui/components/cards/EntityListCard';
import {
  type Experience,
  useExperiences,
  useCreateExperience,
  useDeleteExperience,
  useUpdateExperience,
} from '../hooks/experiences';
import { useState } from 'react';
import Input from '@repo/ui/components/Input';
import Button from '@repo/ui/components/Button';
import { useSecurityDomain } from '../hooks/auth';

export default function ExperiencesEntityCard() {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const { data, isLoading } = useExperiences({
    searchQuery,
    page: currentPage,
  });

  const { isAllowed: canCreate } = useSecurityDomain({
    routeObject: 'experiences',
    action: 'create',
  });

  const { isAllowed: canUpdate } = useSecurityDomain({
    routeObject: 'experiences',
    action: 'update',
  });

  return (
    <EntityListCard
      isLoading={isLoading}
      title="Experiences"
      onSearchQueryChange={setSearchQuery}
      items={data?.experiences ?? []}
      itemToEntityItem={item => ({
        key: item.experienceid,
        title: item.experiencetitle,
        subtitle: item.experiencedesc,
        onClick: () => {},
      })}
      onPageChange={setCurrentPage}
      currentPage={currentPage}
      totalPages={data?.totalPages}
      AddOrUpdateItemFormComponent={AddOrUpdateExperienceForm}
      canCreate={canCreate}
      canUpdate={canUpdate}
    />
  );
}

function AddOrUpdateExperienceForm({
  item,
  onCreate,
  onUpdate,
  onDelete,
}: {
  item?: Experience;
  onCreate: () => void;
  onUpdate: () => void;
  onDelete: () => void;
}) {
  const { mutate: createExperience } = useCreateExperience({
    onSuccess: onCreate,
  });

  const { mutate: updateExperience } = useUpdateExperience({
    onSuccess: onUpdate,
  });

  const { mutate: deleteExperience } = useDeleteExperience({
    onSuccess: onDelete,
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData.entries());
    if (item) {
      updateExperience({
        ...item,
        ...data,
      } as Experience);
    } else {
      createExperience(data as Experience);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4">
      <Input label="Title" name="experiencetitle" defaultValue={item?.experiencetitle} />
      <Input
        isTextarea
        label="Description"
        name="experiencedesc"
        defaultValue={item?.experiencedesc}
      />
      <div className="space-x-4">
        <Button>{item ? 'Update Experience' : 'Add Experience'}</Button>
        {item && (
          <button
            onClick={() => deleteExperience(item.experienceid)}
            type="button"
            className="rounded-md border border-red-500 px-4 py-2 text-sm text-red-500"
          >
            Delete Experience
          </button>
        )}
      </div>
    </form>
  );
}
